import * as React from "react";
import { useContext, useEffect } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { detectIncognito } from "utils/detectIncognito";

import LocalStorage from "utils/localStorage";
import { Store } from "hooks/main_store";
import Model from "hooks/Model";

import Page404Screen from "./page404";
import LandingScreen from "./landing";
import WelcomeScreen from "./welcome";

import HomeScreen from "./home";
import InvestScreen from "./invest";
import AccessAccountScreen from "./home/AccessAccount";

import SendScreen from "./send";
import ReceiveScreen from "./receive";
import HistoryListScreen from "./history";

import LegalScreen from "./legal/renderLegalDoc";

import ProfileVideosScreen from "./vwin/ProfileVideos";
import DetailVideoScreen from "./vwin/DetailVideo";
import LayerLoadVideoScreen from "./vwin/DetailVideo/LayerLoadVideo";
import RankingScreen from "./vwin/Ranking";
import DetailPostScreen from "./vwin/DetailPost";
import BlogScreen from "./vwin/Blog";

import RedeemInitialScreen from "./redeem";
import AdminListViewScreen from "./redeem/AdminListView";

import ChannelShortLinksScreen from "./profile/channelShortLinks";

import AuthAction from "./profile/auth-action";
import { Layout } from "components";
import ThreadsAuth from "./profile/threads-auth";

export const RoutesPaths = {
  home: "/",
  welcome: "/welcome",
  invest: "/invest",
  send: "/send",
  receive: "/receive",
  history: "/history",
  accesAccount: "/access-account",
  detailVideo: "/detail-video",
  detailVideoReward: "/detail-video-reward",
  ranking: "/ranking",
  profileVideos: "/profile-videos",
  legal: "/legal",
  redeem: "/redeem",
  authAction: "/auth-action",
  links: "/links",
  detailPost: "/detail-post",
  blog: "/blog",
  admin: "/admin",
  threadsAuth: "/threads-auth",
};
const returnStackCommonRoutes = (stack: "user" | "guest" | "welcome") => {
  return [
    {
      path: RoutesPaths.welcome,
      element: stack === "welcome" ? <WelcomeScreen /> : <HomeScreen />,
    },
    {
      path: RoutesPaths.home,
      element: <LandingScreen />,
      index: true,
    },
    {
      path: RoutesPaths.invest,
      element: <InvestScreen />,
    },
    {
      path: RoutesPaths.detailVideo,
      element:
        stack === "user" ? <LayerLoadVideoScreen /> : <DetailVideoScreen />,
    },
    {
      path: RoutesPaths.detailVideoReward,
      element: <DetailVideoScreen />,
    },
    {
      path: RoutesPaths.profileVideos,
      element: <ProfileVideosScreen />,
    },
    {
      path: RoutesPaths.legal,
      element: <LegalScreen />,
    },
    {
      path: RoutesPaths.links,
      element: <ChannelShortLinksScreen />,
    },
    {
      path: RoutesPaths.authAction,
      element: <AuthAction />,
    },
    {
      path: RoutesPaths.detailPost,
      element: <DetailPostScreen />,
    },
    {
      path: RoutesPaths.blog,
      element: <BlogScreen />,
    },
    {
      path: RoutesPaths.admin,
      element: <AdminListViewScreen />,
    },
    {
      path: RoutesPaths.threadsAuth,
      element: <ThreadsAuth />,
    },
    {
      path: "*",
      element: <Page404Screen />,
    },
  ];
};

const userRouter = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    children: [
      {
        path: RoutesPaths.send,
        element: <SendScreen />,
      },
      {
        path: RoutesPaths.receive,
        element: <ReceiveScreen />,
      },
      {
        path: RoutesPaths.history,
        element: <HistoryListScreen />,
      },
      {
        path: RoutesPaths.accesAccount,
        element: <AccessAccountScreen />,
      },
      {
        path: RoutesPaths.ranking,
        element: <RankingScreen />,
      },
      {
        path: RoutesPaths.redeem,
        element: <RedeemInitialScreen />,
      },
      ...returnStackCommonRoutes("user"),
    ],
  },
]);

const guestRouter = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    children: [...returnStackCommonRoutes("guest")],
  },
]);

const welcomeRouter = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    children: [...returnStackCommonRoutes("welcome")],
  },
]);

const Routes = () => {
  const { state } = useContext(Store);
  const urlParams = new URLSearchParams(window.location.search);
  const welcomePage = urlParams.get("page");

  const initialLoad = async () => {
    try {
      const result = await detectIncognito();

      const isOlder = LocalStorage(result.isPrivate).getItem("isOlder");
      const completedWelcome = LocalStorage(result.isPrivate).getItem(
        "completedWelcome",
      );

      if (isOlder === "true") {
        Model.setData("isOlder", true);
      }

      if (completedWelcome === "true") {
        Model.setData("completedWelcome", true);
      }
    } catch (error) {
      Model.updateAlerts({
        message: error?.message || error,
        variant: "error",
      });
    }
  };

  const returnRouter = React.useCallback(() => {
    if (state?.infoUser?.uid && !welcomePage) {
      return userRouter;
    } else if (state?.completedWelcome && !welcomePage) {
      return guestRouter;
    }

    return welcomeRouter;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, welcomeRouter]);

  useEffect(() => {
    initialLoad();
  }, []);

  return <RouterProvider router={returnRouter()} />;
};

export default Routes;
